import { useMeQuery } from '@/remote/user-management/fetch-me';
import type { SiteComprehensiveStatus, SiteData } from '@/types/site';

interface SiteTableProps {
  sites: SiteData[];
  comprehensiveStatusData?: Record<string, SiteComprehensiveStatus>; // siteId를 키로 하는 종합 상태 데이터
  onSiteClick: (site: SiteData) => void;
  onInspectionClick: (site: SiteData, event: React.MouseEvent) => void;
  onDetailClick: (site: SiteData, event: React.MouseEvent) => void;
  onControlClick: (site: SiteData, event: React.MouseEvent) => void;
  onSort: (field: string) => void;
  renderSortIcon: (field: string) => React.ReactNode;
  searchTerm: string;
  filteredCount: number;
  isComprehensiveStatusLoading: boolean;
}

export default function SiteTable({
  sites,
  comprehensiveStatusData = {},
  isComprehensiveStatusLoading,
  onSiteClick,
  onInspectionClick,
  onDetailClick,
  onControlClick,
  onSort,
  renderSortIcon,
}: SiteTableProps) {
  const { data: me } = useMeQuery();
  const getMotorColor = (operation: string) => {
    switch (operation) {
      case '운전':
        return 'text-green-600';
      case '정지':
        return 'text-red-600';
      case '대기':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  const getErrorStatusColor = (hasError: boolean) => {
    return hasError ? 'text-red-600 bg-red-50' : 'text-green-600 bg-green-50';
  };

  // const formatTemperature = (temp: number) => {
  //   return `${temp.toFixed(1)}°C`;
  // };

  // const formatInflow = (inflow: number) => {
  //   return `${inflow.toFixed(2)}L/min`;
  // };

  const getComprehensiveStatus = (site: SiteData) => {
    return comprehensiveStatusData[site.siteId];
  };

  const getActualMotorStatus = (site: SiteData) => {
    const comprehensiveStatus = getComprehensiveStatus(site);
    if (comprehensiveStatus?.motorStatus?.mainMotor) {
      return '운전';
    }
    return '정지';
  };

  const getActualWaterLevel = (site: SiteData) => {
    const comprehensiveStatus = getComprehensiveStatus(site);
    return comprehensiveStatus?.waterLevel?.current || 0;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm  overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => onSort('name')}
              >
                <div className="flex items-center truncate  space-x-1">
                  <span>현장명</span>
                  {renderSortIcon('name')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => onSort('address')}
              >
                <div className="flex items-center  space-x-1">
                  <span>주소</span>
                  {renderSortIcon('address')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider
                 
                 select-none"
                // onClick={() => onSort('motorOperation')}
              >
                <div className="flex items-center  space-x-1 truncate w-[120px]">
                  <span>모터 작동</span>
                  {/* {renderSortIcon('motorOperation')} */}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider  select-none"
                // onClick={() => onSort('waterLevel')}
              >
                <div className="flex items-center  space-x-1 truncate">
                  <span>수위</span>
                  {/* {renderSortIcon('waterLevel')} */}
                </div>
              </th>
              {/* <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => onSort('cumulativeFlow')}
              >
                <div className="flex items-center  space-x-1 w-[70px] truncate">
                  <span>누적 유량</span>
                  {renderSortIcon('cumulativeFlow')}
                </div>
              </th> */}

              {/* <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => onSort('temperature')}
              >
                <div className="flex items-center  space-x-1 truncate">
                  <span>온도</span>
                  {renderSortIcon('temperature')}
                </div>
              </th> */}
              {/* <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => onSort('inflow')}
              >
                <div className="flex items-center  space-x-1 truncate">
                  <span>유입량</span>
                  {renderSortIcon('inflow')}
                </div>
              </th> */}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider truncate">
                에러 상태
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                <div className="flex items-center  space-x-1 truncate">
                  <span>제어</span>
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider truncate">
                점검 기록
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider truncate">
                상세 데이터
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => onSort('installationDate')}
              >
                <div className="flex items-center  space-x-1 truncate">
                  <span>설치일</span>
                  {renderSortIcon('installationDate')}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sites.map((site) => {
              const comprehensiveStatus = getComprehensiveStatus(site);
              const actualMotorStatus = getActualMotorStatus(site);
              const actualWaterLevel = getActualWaterLevel(site);

              const currentWaterLevel =
                comprehensiveStatus?.waterLevel?.current || 0;
              const lowWaterLevel = comprehensiveStatus?.waterLevel?.low || 0;
              const highWaterLevel = comprehensiveStatus?.waterLevel?.high || 0;

              const onError =
                !comprehensiveStatus?.hasNoError ||
                comprehensiveStatus?.errorStatus?.electricWaveError ||
                comprehensiveStatus?.errorStatus?.overloadError ||
                comprehensiveStatus?.errorStatus?.undergroundWaterError ||
                currentWaterLevel < lowWaterLevel ||
                currentWaterLevel > highWaterLevel;

              return (
                <tr
                  key={site.siteId}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => onSiteClick(site)}
                >
                  <td className="px-6 py-4 min-w-40 whitespace-break-spaces">
                    <div className="space-y-2">
                      <div className="font-medium text-gray-900 min-w-[300px] max-w-[400px] line-clamp-2">
                        {site.name}
                      </div>
                      <div className="flex items-center space-x-2 flex-wrap gap-2"></div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-break-spaces">
                    <div className="text-sm text-gray-900 line-clamp-2 min-w-[300px] max-w-[400px]">
                      {site.address}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap w-[120px]">
                    {isComprehensiveStatusLoading ? (
                      <div className="flex justify-start items-center">
                        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : (
                      <div
                        className={`flex items-center space-x-2 ${getMotorColor(actualMotorStatus)} w-full`}
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i
                            className={`${actualMotorStatus === '운전' ? 'ri-play' : actualMotorStatus === '정지' ? 'ri-stop' : 'ri-pause'}-circle-line`}
                          ></i>
                        </div>
                        <span className="text-sm font-medium">
                          {actualMotorStatus}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isComprehensiveStatusLoading ? (
                      <div className="flex justify-start items-center">
                        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center space-x-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${actualWaterLevel}%` }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-900">
                            {actualWaterLevel}%
                          </span>
                        </div>
                        {/* {comprehensiveStatus?.waterLevel && (
                          <div className="text-xs text-gray-500 mt-1">
                            저: {comprehensiveStatus.waterLevel.low}% / 고:{' '}
                            {comprehensiveStatus.waterLevel.high}%
                          </div>
                        )} */}
                        {/* {comprehensiveStatus?.undergroundWaterLevel !==
                          undefined && (
                          <div className="text-xs text-blue-600 mt-1">
                            지하수위:{' '}
                            {comprehensiveStatus.undergroundWaterLevel}%
                          </div>
                        )} */}
                      </>
                    )}
                  </td>
                  {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 w-[130px] truncate">
                    {site.cumulativeFlow}
                    {comprehensiveStatus?.accumulatedPulseValue && (
                      <div className="text-xs text-gray-500 mt-1">
                        펄스:{' '}
                        {comprehensiveStatus.accumulatedPulseValue.toLocaleString()}
                      </div>
                    )}
                  </td> */}
                  {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <i className="ri-temp-hot-line text-orange-500 w-4 h-4"></i>
                          <span>
                            {formatTemperature(comprehensiveStatus.temperature)}
                          </span>
                        </div>
                      </div>
                    }
                  </td> */}
                  {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {comprehensiveStatus?.inflow !== undefined ? (
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <i className="ri-water-flash-line text-blue-500 w-4 h-4"></i>
                          <span>
                            {formatInflow(comprehensiveStatus.inflow)}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td> */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isComprehensiveStatusLoading ? (
                      <div className="flex justify-start items-center">
                        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : comprehensiveStatus ? (
                      <div className="space-y-1">
                        <div
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getErrorStatusColor(onError)}`}
                        >
                          <i
                            className={`${onError ? 'ri-alert-line' : 'ri-check-line'} w-3 h-3 mr-1`}
                          ></i>
                          {!onError ? '정상' : '에러'}
                        </div>
                        {onError && (
                          <div className="text-xs text-gray-500 space-y-0.5">
                            {comprehensiveStatus?.errorStatus
                              .electricWaveError && <div>• 전파 이상</div>}
                            {comprehensiveStatus?.errorStatus.overloadError && (
                              <div>• 과부하 이상</div>
                            )}
                            {comprehensiveStatus.errorStatus
                              .undergroundWaterError && (
                              <div>• 지하수 이상</div>
                            )}
                            {currentWaterLevel < lowWaterLevel && (
                              <div>• 저수위 이상</div>
                            )}
                            {currentWaterLevel > highWaterLevel && (
                              <div>• 고수위 이상</div>
                            )}
                            {/* {comprehensiveStatus.errorStatus
                              .circuitBreakerError && (
                              <div>
                                • {site?.alias?.circuitBreaker || '차단기'} 이상
                              </div>
                            )}
                            {comprehensiveStatus.errorStatus.externalError && (
                              <div>
                                • {site?.alias?.external || '외부'} 에러
                              </div>
                            )}
                            {comprehensiveStatus.errorStatus
                              .pressureSensorError && (
                              <div>
                                • {site?.alias?.pressureSensor || '압력센서'}
                                이상
                              </div>
                            )}
                            {comprehensiveStatus.errorStatus.preparation && (
                              <div>
                                • {site?.alias?.preparation || '예비1'} 이상
                              </div>
                            )}
                            {comprehensiveStatus.errorStatus.preparation2 && (
                              <div>
                                • {site?.alias?.preparation2 || '예비2'} 이상
                              </div>
                            )} */}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {/* 제어 버튼 */}
                    {me?.userLevel === 'SUPER_ADMINISTRATOR' ||
                    me?.userLevel === 'HEADQUARTERS_MANAGER' ? (
                      <span className="text-gray-400">
                        <button
                          onClick={(e) => onControlClick(site, e)}
                          className="text-purple-600 hover:text-purple-800 text-sm cursor-pointer whitespace-nowrap flex items-center space-x-1"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-settings-line"></i>
                          </div>
                          <span>제어</span>
                        </button>
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={(e) => onInspectionClick(site, e)}
                      className="text-blue-600 hover:text-blue-800 text-sm cursor-pointer whitespace-nowrap flex items-center space-x-1"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-file-list-3-line"></i>
                      </div>
                      <span>기록 보기</span>
                    </button>
                    {site.lastInspection && (
                      <div className="text-xs text-gray-500 mt-1">
                        최근: {site.lastInspection}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={(e) => onDetailClick(site, e)}
                      className="text-purple-600 hover:text-purple-800 text-sm cursor-pointer whitespace-nowrap flex items-center space-x-1"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-bar-chart-line"></i>
                      </div>
                      <span>상세 보기</span>
                    </button>
                    <div className="text-xs text-gray-500 mt-1">
                      실시간 데이터
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {site.installationDate}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
